/**
 * Gig Economy Integration
 * Vision 2.0 Phase 4: Help gig-seekers find careers with freelance potential
 *
 * Features:
 * - Freelance viability ratings for careers
 * - Gig platform mappings
 * - Income stacking recommendations
 * - Side hustle pathways
 */

/**
 * Freelance viability level
 */
export type FreelanceViability = 'high' | 'medium' | 'low' | 'none';

/**
 * Gig platform categories
 */
export type GigPlatformCategory =
  | 'freelance_marketplace'    // Upwork, Fiverr, Toptal
  | 'creative_services'        // 99designs, Behance, Dribbble
  | 'consulting'               // Clarity.fm, Expert360
  | 'tutoring_education'       // Wyzant, Tutor.com, Outschool
  | 'delivery_transport'       // DoorDash, Uber, Lyft
  | 'home_services'            // TaskRabbit, Thumbtack, Handy
  | 'pet_services'             // Rover, Wag
  | 'healthcare'               // NurseFly, Nomad Health
  | 'writing_content'          // Contently, Medium, Substack
  | 'tech_development'         // Toptal, Gun.io, Arc
  | 'photography_video'        // Shutterstock, Getty, Pexels
  | 'voice_audio'              // Voices.com, ACX, Fiverr
  | 'fitness_wellness'         // Trainerize, TrueCoach
  | 'crafts_handmade'          // Etsy, Amazon Handmade
  | 'professional_services';   // Catalant, Business Talent Group

/**
 * Gig platform information
 */
export interface GigPlatform {
  id: string;
  name: string;
  category: GigPlatformCategory;
  url: string;
  description: string;
  avg_hourly_range: { min: number; max: number };
  startup_difficulty: 'easy' | 'medium' | 'hard';
  income_potential: 'low' | 'medium' | 'high' | 'very_high';
  requirements?: string[];
  best_for?: string[];
}

/**
 * Career freelance profile
 */
export interface CareerFreelanceProfile {
  career_id: string;
  freelance_viability: FreelanceViability;
  viability_score: number; // 0-100
  gig_platforms: string[]; // Platform IDs
  side_hustle_potential: boolean;
  income_stacking_compatible: boolean;
  remote_freelance: boolean;
  startup_investment: 'none' | 'low' | 'medium' | 'high';
  time_to_first_client: string; // e.g., "1-2 weeks", "1-3 months"
  typical_project_types: string[];
  income_range_freelance: { min: number; max: number }; // Annual if full-time freelance
  side_hustle_income: { min: number; max: number }; // Monthly as side gig
}

/**
 * Income stacking recommendation
 */
export interface IncomeStackRecommendation {
  primary_career: string;
  side_gigs: {
    career_id: string;
    platform_ids: string[];
    estimated_hours_per_week: number;
    estimated_monthly_income: { min: number; max: number };
    synergy_score: number; // How well it complements primary career
  }[];
  total_estimated_monthly: { min: number; max: number };
  recommended_schedule: string;
}

/**
 * All gig platforms database
 */
export const GIG_PLATFORMS: Record<string, GigPlatform> = {
  // Freelance Marketplaces
  upwork: {
    id: 'upwork',
    name: 'Upwork',
    category: 'freelance_marketplace',
    url: 'https://upwork.com',
    description: 'Largest freelance marketplace for all skill types',
    avg_hourly_range: { min: 15, max: 150 },
    startup_difficulty: 'medium',
    income_potential: 'high',
    requirements: ['Portfolio or work samples', 'Profile approval'],
    best_for: ['Writers', 'Designers', 'Developers', 'Virtual Assistants'],
  },
  fiverr: {
    id: 'fiverr',
    name: 'Fiverr',
    category: 'freelance_marketplace',
    url: 'https://fiverr.com',
    description: 'Gig-based marketplace for quick services',
    avg_hourly_range: { min: 10, max: 100 },
    startup_difficulty: 'easy',
    income_potential: 'medium',
    requirements: ['Service listings'],
    best_for: ['Graphic Design', 'Writing', 'Voice Over', 'Video Editing'],
  },
  toptal: {
    id: 'toptal',
    name: 'Toptal',
    category: 'tech_development',
    url: 'https://toptal.com',
    description: 'Elite network for top 3% of freelance talent',
    avg_hourly_range: { min: 60, max: 200 },
    startup_difficulty: 'hard',
    income_potential: 'very_high',
    requirements: ['Rigorous screening process', 'Expert-level skills'],
    best_for: ['Senior Developers', 'Designers', 'Finance Experts'],
  },

  // Creative Services
  '99designs': {
    id: '99designs',
    name: '99designs',
    category: 'creative_services',
    url: 'https://99designs.com',
    description: 'Design contest and direct hire platform',
    avg_hourly_range: { min: 25, max: 100 },
    startup_difficulty: 'medium',
    income_potential: 'medium',
    requirements: ['Design portfolio'],
    best_for: ['Logo Design', 'Brand Identity', 'Web Design'],
  },
  behance: {
    id: 'behance',
    name: 'Behance',
    category: 'creative_services',
    url: 'https://behance.net',
    description: 'Adobe creative portfolio and job platform',
    avg_hourly_range: { min: 30, max: 150 },
    startup_difficulty: 'medium',
    income_potential: 'high',
    requirements: ['Creative portfolio'],
    best_for: ['Graphic Designers', 'Photographers', 'Illustrators'],
  },

  // Writing & Content
  contently: {
    id: 'contently',
    name: 'Contently',
    category: 'writing_content',
    url: 'https://contently.com',
    description: 'Premium content marketing platform',
    avg_hourly_range: { min: 50, max: 200 },
    startup_difficulty: 'hard',
    income_potential: 'high',
    requirements: ['Published writing samples', 'Application approval'],
    best_for: ['Content Writers', 'Journalists', 'Copywriters'],
  },
  medium: {
    id: 'medium',
    name: 'Medium Partner Program',
    category: 'writing_content',
    url: 'https://medium.com',
    description: 'Earn from your writing based on reader engagement',
    avg_hourly_range: { min: 5, max: 100 },
    startup_difficulty: 'easy',
    income_potential: 'medium',
    requirements: ['100 followers minimum'],
    best_for: ['Bloggers', 'Thought Leaders', 'Niche Writers'],
  },
  substack: {
    id: 'substack',
    name: 'Substack',
    category: 'writing_content',
    url: 'https://substack.com',
    description: 'Newsletter platform with paid subscriptions',
    avg_hourly_range: { min: 10, max: 500 },
    startup_difficulty: 'medium',
    income_potential: 'very_high',
    requirements: ['Audience building'],
    best_for: ['Newsletter Writers', 'Subject Matter Experts'],
  },

  // Tutoring & Education
  wyzant: {
    id: 'wyzant',
    name: 'Wyzant',
    category: 'tutoring_education',
    url: 'https://wyzant.com',
    description: 'One-on-one tutoring marketplace',
    avg_hourly_range: { min: 20, max: 80 },
    startup_difficulty: 'easy',
    income_potential: 'medium',
    requirements: ['Subject expertise', 'Background check'],
    best_for: ['Teachers', 'Subject Matter Experts', 'Test Prep'],
  },
  outschool: {
    id: 'outschool',
    name: 'Outschool',
    category: 'tutoring_education',
    url: 'https://outschool.com',
    description: 'Live online classes for K-12 students',
    avg_hourly_range: { min: 25, max: 60 },
    startup_difficulty: 'medium',
    income_potential: 'medium',
    requirements: ['Class proposals', 'Teaching ability'],
    best_for: ['Teachers', 'Subject Experts', 'Creative Instructors'],
  },
  coursera: {
    id: 'coursera',
    name: 'Coursera Instructor',
    category: 'tutoring_education',
    url: 'https://coursera.org',
    description: 'Create courses for global learners',
    avg_hourly_range: { min: 50, max: 200 },
    startup_difficulty: 'hard',
    income_potential: 'high',
    requirements: ['Subject matter expertise', 'Course creation skills'],
    best_for: ['Professors', 'Industry Experts', 'Professional Trainers'],
  },

  // Home Services
  taskrabbit: {
    id: 'taskrabbit',
    name: 'TaskRabbit',
    category: 'home_services',
    url: 'https://taskrabbit.com',
    description: 'Local odd jobs and handyman services',
    avg_hourly_range: { min: 20, max: 80 },
    startup_difficulty: 'easy',
    income_potential: 'medium',
    requirements: ['Skills assessment', 'Background check'],
    best_for: ['Handymen', 'Movers', 'Cleaners', 'Assemblers'],
  },
  thumbtack: {
    id: 'thumbtack',
    name: 'Thumbtack',
    category: 'home_services',
    url: 'https://thumbtack.com',
    description: 'Local services marketplace',
    avg_hourly_range: { min: 25, max: 100 },
    startup_difficulty: 'easy',
    income_potential: 'medium',
    requirements: ['Service listing', 'Reviews'],
    best_for: ['Contractors', 'Cleaners', 'Personal Services'],
  },

  // Delivery & Transport
  doordash: {
    id: 'doordash',
    name: 'DoorDash',
    category: 'delivery_transport',
    url: 'https://doordash.com',
    description: 'Food delivery platform',
    avg_hourly_range: { min: 15, max: 25 },
    startup_difficulty: 'easy',
    income_potential: 'low',
    requirements: ['Vehicle', 'Background check', 'Valid license'],
    best_for: ['Flexible workers', 'Part-time income'],
  },
  uber: {
    id: 'uber',
    name: 'Uber',
    category: 'delivery_transport',
    url: 'https://uber.com',
    description: 'Rideshare and delivery platform',
    avg_hourly_range: { min: 15, max: 30 },
    startup_difficulty: 'easy',
    income_potential: 'medium',
    requirements: ['Vehicle', 'Insurance', 'Background check'],
    best_for: ['Drivers', 'Flexible schedules'],
  },
  instacart: {
    id: 'instacart',
    name: 'Instacart',
    category: 'delivery_transport',
    url: 'https://instacart.com',
    description: 'Grocery shopping and delivery',
    avg_hourly_range: { min: 12, max: 25 },
    startup_difficulty: 'easy',
    income_potential: 'low',
    requirements: ['Smartphone', 'Vehicle'],
    best_for: ['Shoppers', 'Part-time workers'],
  },

  // Pet Services
  rover: {
    id: 'rover',
    name: 'Rover',
    category: 'pet_services',
    url: 'https://rover.com',
    description: 'Pet sitting and dog walking platform',
    avg_hourly_range: { min: 15, max: 50 },
    startup_difficulty: 'easy',
    income_potential: 'medium',
    requirements: ['Pet experience', 'Background check'],
    best_for: ['Animal lovers', 'Dog walkers', 'Pet sitters'],
  },
  wag: {
    id: 'wag',
    name: 'Wag!',
    category: 'pet_services',
    url: 'https://wagwalking.com',
    description: 'Dog walking and pet care app',
    avg_hourly_range: { min: 15, max: 30 },
    startup_difficulty: 'easy',
    income_potential: 'low',
    requirements: ['Dog handling test', 'Background check'],
    best_for: ['Dog walkers', 'Part-time pet care'],
  },

  // Tech Development
  gun_io: {
    id: 'gun_io',
    name: 'Gun.io',
    category: 'tech_development',
    url: 'https://gun.io',
    description: 'Vetted freelance developers',
    avg_hourly_range: { min: 75, max: 200 },
    startup_difficulty: 'hard',
    income_potential: 'very_high',
    requirements: ['Technical assessment', 'Experience'],
    best_for: ['Senior Developers', 'Full-stack Engineers'],
  },
  github_jobs: {
    id: 'github_jobs',
    name: 'GitHub/Remote Jobs',
    category: 'tech_development',
    url: 'https://github.com/jobs',
    description: 'Developer job board with remote options',
    avg_hourly_range: { min: 50, max: 150 },
    startup_difficulty: 'medium',
    income_potential: 'high',
    requirements: ['GitHub profile', 'Portfolio'],
    best_for: ['Developers', 'Open Source Contributors'],
  },

  // Photography & Video
  shutterstock: {
    id: 'shutterstock',
    name: 'Shutterstock Contributor',
    category: 'photography_video',
    url: 'https://submit.shutterstock.com',
    description: 'Sell stock photos and videos',
    avg_hourly_range: { min: 5, max: 50 },
    startup_difficulty: 'easy',
    income_potential: 'medium',
    requirements: ['Quality content', 'Model releases'],
    best_for: ['Photographers', 'Videographers'],
  },
  getty: {
    id: 'getty',
    name: 'Getty Images',
    category: 'photography_video',
    url: 'https://gettyimages.com',
    description: 'Premium stock photography',
    avg_hourly_range: { min: 20, max: 100 },
    startup_difficulty: 'hard',
    income_potential: 'high',
    requirements: ['Application approval', 'Professional quality'],
    best_for: ['Professional Photographers', 'Editorial'],
  },

  // Voice & Audio
  voices_com: {
    id: 'voices_com',
    name: 'Voices.com',
    category: 'voice_audio',
    url: 'https://voices.com',
    description: 'Voice over talent marketplace',
    avg_hourly_range: { min: 50, max: 300 },
    startup_difficulty: 'medium',
    income_potential: 'high',
    requirements: ['Demo reel', 'Home studio'],
    best_for: ['Voice Actors', 'Narrators', 'Commercial Talent'],
  },
  acx: {
    id: 'acx',
    name: 'ACX (Audiobook Creation)',
    category: 'voice_audio',
    url: 'https://acx.com',
    description: 'Audiobook narration platform',
    avg_hourly_range: { min: 50, max: 200 },
    startup_difficulty: 'medium',
    income_potential: 'medium',
    requirements: ['Narration samples', 'Recording equipment'],
    best_for: ['Narrators', 'Voice Actors'],
  },

  // Fitness & Wellness
  trainerize: {
    id: 'trainerize',
    name: 'Trainerize',
    category: 'fitness_wellness',
    url: 'https://trainerize.com',
    description: 'Online personal training platform',
    avg_hourly_range: { min: 30, max: 100 },
    startup_difficulty: 'medium',
    income_potential: 'medium',
    requirements: ['Fitness certification', 'Training programs'],
    best_for: ['Personal Trainers', 'Fitness Coaches'],
  },
  mindbody: {
    id: 'mindbody',
    name: 'Mindbody',
    category: 'fitness_wellness',
    url: 'https://mindbody.io',
    description: 'Wellness business management',
    avg_hourly_range: { min: 25, max: 80 },
    startup_difficulty: 'medium',
    income_potential: 'medium',
    requirements: ['Wellness certification'],
    best_for: ['Yoga Instructors', 'Wellness Coaches', 'Massage Therapists'],
  },

  // Crafts & Handmade
  etsy: {
    id: 'etsy',
    name: 'Etsy',
    category: 'crafts_handmade',
    url: 'https://etsy.com',
    description: 'Handmade and vintage marketplace',
    avg_hourly_range: { min: 10, max: 100 },
    startup_difficulty: 'easy',
    income_potential: 'medium',
    requirements: ['Handmade products', 'Shop setup'],
    best_for: ['Crafters', 'Artists', 'Vintage Sellers'],
  },
  amazon_handmade: {
    id: 'amazon_handmade',
    name: 'Amazon Handmade',
    category: 'crafts_handmade',
    url: 'https://services.amazon.com/handmade',
    description: 'Handmade goods on Amazon',
    avg_hourly_range: { min: 15, max: 100 },
    startup_difficulty: 'medium',
    income_potential: 'high',
    requirements: ['Artisan application', 'Handmade products'],
    best_for: ['Artisans', 'Crafters', 'Jewelry Makers'],
  },

  // Professional Services
  catalant: {
    id: 'catalant',
    name: 'Catalant',
    category: 'professional_services',
    url: 'https://catalant.com',
    description: 'Expert marketplace for business consulting',
    avg_hourly_range: { min: 100, max: 500 },
    startup_difficulty: 'hard',
    income_potential: 'very_high',
    requirements: ['MBA or equivalent', 'Consulting experience'],
    best_for: ['Strategy Consultants', 'Business Experts'],
  },
  clarity_fm: {
    id: 'clarity_fm',
    name: 'Clarity.fm',
    category: 'consulting',
    url: 'https://clarity.fm',
    description: 'Expert advice calls marketplace',
    avg_hourly_range: { min: 50, max: 500 },
    startup_difficulty: 'medium',
    income_potential: 'high',
    requirements: ['Expertise in a field'],
    best_for: ['Entrepreneurs', 'Industry Experts', 'Mentors'],
  },
};

/**
 * Career-to-freelance profile mappings
 * Maps career IDs to their freelance potential
 */
export const CAREER_FREELANCE_PROFILES: Record<string, CareerFreelanceProfile> = {
  // High freelance viability careers
  graphic_designer: {
    career_id: 'graphic_designer',
    freelance_viability: 'high',
    viability_score: 95,
    gig_platforms: ['upwork', 'fiverr', '99designs', 'behance'],
    side_hustle_potential: true,
    income_stacking_compatible: true,
    remote_freelance: true,
    startup_investment: 'low',
    time_to_first_client: '1-2 weeks',
    typical_project_types: ['Logo design', 'Brand identity', 'Social media graphics', 'Print materials'],
    income_range_freelance: { min: 45000, max: 120000 },
    side_hustle_income: { min: 500, max: 3000 },
  },
  web_developer: {
    career_id: 'web_developer',
    freelance_viability: 'high',
    viability_score: 90,
    gig_platforms: ['upwork', 'toptal', 'gun_io', 'github_jobs'],
    side_hustle_potential: true,
    income_stacking_compatible: true,
    remote_freelance: true,
    startup_investment: 'low',
    time_to_first_client: '2-4 weeks',
    typical_project_types: ['Website development', 'Web apps', 'E-commerce', 'Maintenance'],
    income_range_freelance: { min: 60000, max: 180000 },
    side_hustle_income: { min: 1000, max: 5000 },
  },
  technical_writer: {
    career_id: 'technical_writer',
    freelance_viability: 'high',
    viability_score: 85,
    gig_platforms: ['upwork', 'contently', 'medium'],
    side_hustle_potential: true,
    income_stacking_compatible: true,
    remote_freelance: true,
    startup_investment: 'none',
    time_to_first_client: '2-4 weeks',
    typical_project_types: ['Documentation', 'User guides', 'API docs', 'Blog posts'],
    income_range_freelance: { min: 50000, max: 100000 },
    side_hustle_income: { min: 500, max: 2500 },
  },
  video_editor: {
    career_id: 'video_editor',
    freelance_viability: 'high',
    viability_score: 88,
    gig_platforms: ['upwork', 'fiverr', 'behance'],
    side_hustle_potential: true,
    income_stacking_compatible: true,
    remote_freelance: true,
    startup_investment: 'medium',
    time_to_first_client: '1-3 weeks',
    typical_project_types: ['YouTube videos', 'Corporate videos', 'Social media content', 'Commercials'],
    income_range_freelance: { min: 40000, max: 100000 },
    side_hustle_income: { min: 500, max: 3000 },
  },
  photographer: {
    career_id: 'photographer',
    freelance_viability: 'high',
    viability_score: 85,
    gig_platforms: ['shutterstock', 'getty', 'behance', 'thumbtack'],
    side_hustle_potential: true,
    income_stacking_compatible: true,
    remote_freelance: false,
    startup_investment: 'high',
    time_to_first_client: '2-4 weeks',
    typical_project_types: ['Portraits', 'Events', 'Product photography', 'Stock photos'],
    income_range_freelance: { min: 35000, max: 90000 },
    side_hustle_income: { min: 500, max: 2500 },
  },
  personal_trainer: {
    career_id: 'personal_trainer',
    freelance_viability: 'high',
    viability_score: 80,
    gig_platforms: ['trainerize', 'mindbody', 'thumbtack'],
    side_hustle_potential: true,
    income_stacking_compatible: true,
    remote_freelance: true, // Online training
    startup_investment: 'low',
    time_to_first_client: '1-2 weeks',
    typical_project_types: ['Personal training', 'Online coaching', 'Group classes', 'Nutrition plans'],
    income_range_freelance: { min: 40000, max: 100000 },
    side_hustle_income: { min: 500, max: 2000 },
  },

  // Medium freelance viability
  social_media_manager: {
    career_id: 'social_media_manager',
    freelance_viability: 'high',
    viability_score: 82,
    gig_platforms: ['upwork', 'fiverr'],
    side_hustle_potential: true,
    income_stacking_compatible: true,
    remote_freelance: true,
    startup_investment: 'none',
    time_to_first_client: '2-4 weeks',
    typical_project_types: ['Account management', 'Content creation', 'Strategy', 'Ads management'],
    income_range_freelance: { min: 40000, max: 80000 },
    side_hustle_income: { min: 500, max: 2000 },
  },
  tutor: {
    career_id: 'tutor',
    freelance_viability: 'high',
    viability_score: 85,
    gig_platforms: ['wyzant', 'outschool', 'coursera'],
    side_hustle_potential: true,
    income_stacking_compatible: true,
    remote_freelance: true,
    startup_investment: 'none',
    time_to_first_client: '1-2 weeks',
    typical_project_types: ['One-on-one tutoring', 'Test prep', 'Online courses', 'Group sessions'],
    income_range_freelance: { min: 30000, max: 70000 },
    side_hustle_income: { min: 400, max: 1500 },
  },
  copywriter: {
    career_id: 'copywriter',
    freelance_viability: 'high',
    viability_score: 88,
    gig_platforms: ['upwork', 'contently', 'fiverr', 'medium', 'substack'],
    side_hustle_potential: true,
    income_stacking_compatible: true,
    remote_freelance: true,
    startup_investment: 'none',
    time_to_first_client: '2-3 weeks',
    typical_project_types: ['Website copy', 'Sales pages', 'Email campaigns', 'Ad copy'],
    income_range_freelance: { min: 45000, max: 120000 },
    side_hustle_income: { min: 500, max: 3000 },
  },
  handyman: {
    career_id: 'handyman',
    freelance_viability: 'high',
    viability_score: 85,
    gig_platforms: ['taskrabbit', 'thumbtack'],
    side_hustle_potential: true,
    income_stacking_compatible: true,
    remote_freelance: false,
    startup_investment: 'medium',
    time_to_first_client: '1-2 weeks',
    typical_project_types: ['Repairs', 'Assembly', 'Maintenance', 'Small renovations'],
    income_range_freelance: { min: 40000, max: 80000 },
    side_hustle_income: { min: 500, max: 2000 },
  },

  // Medium-low freelance viability
  customer_service_representative: {
    career_id: 'customer_service_representative',
    freelance_viability: 'medium',
    viability_score: 50,
    gig_platforms: ['upwork'],
    side_hustle_potential: true,
    income_stacking_compatible: true,
    remote_freelance: true,
    startup_investment: 'none',
    time_to_first_client: '2-4 weeks',
    typical_project_types: ['Virtual assistant', 'Chat support', 'Email support'],
    income_range_freelance: { min: 25000, max: 50000 },
    side_hustle_income: { min: 300, max: 1000 },
  },
  groomer: {
    career_id: 'groomer',
    freelance_viability: 'medium',
    viability_score: 65,
    gig_platforms: ['rover', 'thumbtack'],
    side_hustle_potential: true,
    income_stacking_compatible: false,
    remote_freelance: false,
    startup_investment: 'medium',
    time_to_first_client: '2-4 weeks',
    typical_project_types: ['Dog grooming', 'Cat grooming', 'Mobile grooming'],
    income_range_freelance: { min: 30000, max: 60000 },
    side_hustle_income: { min: 300, max: 1000 },
  },
  driver: {
    career_id: 'driver',
    freelance_viability: 'medium',
    viability_score: 60,
    gig_platforms: ['uber', 'doordash', 'instacart'],
    side_hustle_potential: true,
    income_stacking_compatible: true,
    remote_freelance: false,
    startup_investment: 'low',
    time_to_first_client: '1 week',
    typical_project_types: ['Rideshare', 'Food delivery', 'Grocery delivery'],
    income_range_freelance: { min: 25000, max: 50000 },
    side_hustle_income: { min: 400, max: 1500 },
  },

  // Low freelance viability
  electrician: {
    career_id: 'electrician',
    freelance_viability: 'medium',
    viability_score: 55,
    gig_platforms: ['thumbtack', 'taskrabbit'],
    side_hustle_potential: true,
    income_stacking_compatible: false,
    remote_freelance: false,
    startup_investment: 'high',
    time_to_first_client: '1-3 months',
    typical_project_types: ['Residential electrical', 'Small repairs', 'Installations'],
    income_range_freelance: { min: 50000, max: 90000 },
    side_hustle_income: { min: 500, max: 2000 },
  },
  plumber: {
    career_id: 'plumber',
    freelance_viability: 'medium',
    viability_score: 55,
    gig_platforms: ['thumbtack', 'taskrabbit'],
    side_hustle_potential: true,
    income_stacking_compatible: false,
    remote_freelance: false,
    startup_investment: 'high',
    time_to_first_client: '1-3 months',
    typical_project_types: ['Residential plumbing', 'Emergency repairs', 'Installations'],
    income_range_freelance: { min: 50000, max: 90000 },
    side_hustle_income: { min: 500, max: 2000 },
  },
};

/**
 * Get freelance profile for a career
 */
export function getFreelanceProfile(careerId: string): CareerFreelanceProfile | null {
  return CAREER_FREELANCE_PROFILES[careerId] || null;
}

/**
 * Get gig platforms for a career
 */
export function getGigPlatformsForCareer(careerId: string): GigPlatform[] {
  const profile = CAREER_FREELANCE_PROFILES[careerId];
  if (!profile) return [];

  return profile.gig_platforms
    .map(platformId => GIG_PLATFORMS[platformId])
    .filter((p): p is GigPlatform => p !== undefined);
}

/**
 * Calculate freelance potential score for a career
 * Returns 0-100 based on how viable freelancing is
 */
export function calculateFreelancePotential(careerId: string): {
  score: number;
  viability: FreelanceViability;
  platforms: GigPlatform[];
  profile: CareerFreelanceProfile | null;
} {
  const profile = CAREER_FREELANCE_PROFILES[careerId];

  if (!profile) {
    return {
      score: 0,
      viability: 'none',
      platforms: [],
      profile: null,
    };
  }

  const platforms = getGigPlatformsForCareer(careerId);

  return {
    score: profile.viability_score,
    viability: profile.freelance_viability,
    platforms,
    profile,
  };
}

/**
 * Get income stacking recommendations
 * Suggests side gigs that complement a primary career
 */
export function getIncomeStackRecommendations(
  primaryCareerId: string,
  maxSideGigs = 3
): IncomeStackRecommendation | null {
  const primaryProfile = CAREER_FREELANCE_PROFILES[primaryCareerId];

  // Find compatible side gigs
  const compatibleGigs: IncomeStackRecommendation['side_gigs'] = [];

  // Look for careers that are:
  // 1. Income stacking compatible
  // 2. Different from primary
  // 3. Can be done part-time
  for (const [careerId, profile] of Object.entries(CAREER_FREELANCE_PROFILES)) {
    if (careerId === primaryCareerId) continue;
    if (!profile.income_stacking_compatible) continue;
    if (!profile.side_hustle_potential) continue;

    // Calculate synergy score based on skill overlap and time flexibility
    let synergy = 50;

    // Boost for remote freelance (time flexibility)
    if (profile.remote_freelance) synergy += 20;

    // Boost for quick startup
    if (profile.startup_investment === 'none' || profile.startup_investment === 'low') {
      synergy += 15;
    }

    // Boost for fast first client
    if (profile.time_to_first_client.includes('week')) {
      synergy += 15;
    }

    compatibleGigs.push({
      career_id: careerId,
      platform_ids: profile.gig_platforms.slice(0, 2),
      estimated_hours_per_week: profile.remote_freelance ? 5 : 8,
      estimated_monthly_income: profile.side_hustle_income,
      synergy_score: Math.min(100, synergy),
    });
  }

  // Sort by synergy and take top N
  compatibleGigs.sort((a, b) => b.synergy_score - a.synergy_score);
  const topGigs = compatibleGigs.slice(0, maxSideGigs);

  if (topGigs.length === 0) return null;

  // Calculate totals
  const totalMinMonthly = topGigs.reduce((sum, g) => sum + g.estimated_monthly_income.min, 0);
  const totalMaxMonthly = topGigs.reduce((sum, g) => sum + g.estimated_monthly_income.max, 0);

  return {
    primary_career: primaryCareerId,
    side_gigs: topGigs,
    total_estimated_monthly: { min: totalMinMonthly, max: totalMaxMonthly },
    recommended_schedule: topGigs.length <= 2
      ? 'Evenings and weekends'
      : 'Spread across available hours - consider rotating focus',
  };
}

/**
 * Get viability label for display
 */
export function getViabilityLabel(viability: FreelanceViability): string {
  switch (viability) {
    case 'high':
      return 'High Freelance Potential';
    case 'medium':
      return 'Moderate Freelance Potential';
    case 'low':
      return 'Limited Freelance Options';
    case 'none':
      return 'Not Typically Freelance';
    default:
      return 'Unknown';
  }
}

/**
 * Get startup investment label
 */
export function getInvestmentLabel(investment: CareerFreelanceProfile['startup_investment']): string {
  switch (investment) {
    case 'none':
      return 'No upfront investment needed';
    case 'low':
      return 'Under $500 to start';
    case 'medium':
      return '$500-$2,000 to start';
    case 'high':
      return '$2,000+ investment needed';
    default:
      return 'Varies';
  }
}
